import { forwardRef, useCallback, useState } from 'react';
import { Box, IconButton, keyframes, Paper, Slide, Tooltip, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';

const popIn = keyframes`
    0% { opacity: 0; transform: translateY(40px) scale(0.4); }
    60% { opacity: 1; transform: translateY(-4px) scale(1.05); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
`;
import type { AiChatFloatingProps, AiChatHandle } from './AiChatTypes';
import AiChat from './AiChat';
import TipsAndUpdates from '@mui/icons-material/TipsAndUpdates';

const AiChatFloating = forwardRef<AiChatHandle, AiChatFloatingProps>((props, ref) => {
    const position = { bottom: 18, right: 24, ...props.position };
    const {
        fabContent,
        width = '50vw',
        height = '75vh',
        title,
        borderRadius = 3,
        elevation = 8,
        onVisibleQuestionChange: externalOnVisibleQuestionChange,
        ...aiChatProps
    } = props;

    const [open, setOpen] = useState(false);
    const [visibleQuestion, setVisibleQuestion] = useState<string | null>(null);

    const handleVisibleQuestionChange = useCallback((question: string | null) => {
        let mappedQuestion = question;
        if (props.onRenderPrompt && question) {
            const { promptAsString, json } = props.onRenderPrompt(question)
            mappedQuestion = promptAsString ?? null;
        }
        setVisibleQuestion(mappedQuestion ?? "");
        externalOnVisibleQuestionChange?.(question);
    }, [externalOnVisibleQuestionChange]);

    const displayTitle = visibleQuestion || title || 'Chat';

    const panelBottom = position.bottom ? position.bottom + (props.chatPositionOffSet?.bottom ?? 0) : undefined;
    const panelRight = position.right ? (position.right ?? 0) + (props.chatPositionOffSet?.right ?? 0) : undefined;
    const panelLeft = position.left ? position.left + (props.chatPositionOffSet?.left ?? 0) : undefined;
    const panelTop = position.top != null ? (position.top + 20 + (props.chatPositionOffSet?.top ?? 0)) : undefined;

    return (
        <>
            <Slide direction="up" in={open}>
                <Paper
                    elevation={elevation}
                    sx={{
                        position: 'fixed',
                        bottom: panelTop != null ? undefined : panelBottom,
                        top: panelTop,
                        right: panelRight,
                        left: panelLeft,
                        backgroundColor: '#000000d6',
                        backdropFilter: 'blur(3px)',
                        width,
                        height,
                        borderRadius,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        border: '1px solid gray',
                        zIndex: 10000,
                        visibility: open ? 'visible' : 'hidden',
                        pointerEvents: open ? 'auto' : 'none',
                    }}
                >
                    <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}>
                        <Tooltip title={displayTitle} enterDelay={500}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    flex: 1,
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {displayTitle}
                            </Typography>
                        </Tooltip>
                        <IconButton size="small" onClick={() => setOpen(false)} edge="end" sx={{ ml: 1, flexShrink: 0 }}>
                            <Close fontSize="small" />
                        </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                        <AiChat
                            ref={ref}
                            {...aiChatProps}
                            borderRadius={0}
                            elevation={0}
                            onVisibleQuestionChange={handleVisibleQuestionChange}
                        />
                    </Box>
                </Paper>
            </Slide>

            {!open && (<Box
                sx={{
                    position: 'fixed',
                    ...position,
                    zIndex: 10000,
                    animation: `${popIn} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both`,
                }}
            >
                {fabContent ? (
                    <Box onClick={() => setOpen((o) => !o)} sx={{ cursor: 'pointer' }}>
                        {fabContent}
                    </Box>
                ) : (
                    <Box
                        onClick={() => {
                            setOpen((o) => !o);
                            if (props.onFloatingOpened) props.onFloatingOpened();
                        }}
                        sx={{
                            position: 'relative',
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            backgroundColor: 'primary.dark',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                            '&:hover': {
                                transform: 'scale(1.08)',
                                boxShadow: '0 4px 14px rgba(0,0,0,0.5)',
                            },
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: -8,
                                right: 6,
                                width: 16,
                                height: 16,
                                backgroundColor: 'primary.dark',
                                clipPath: 'polygon(0 0, 100% 0, 100% 100%)',
                                borderRadius: '0 0 2px 0',
                            },
                        }}
                    >
                        <TipsAndUpdates sx={{ color: '#ccc', fontSize: 24 }} />
                        <Box sx={{
                            position: 'absolute',
                            bottom: -16,
                            right: -4,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'primary.dark',
                        }} />
                    </Box>
                )}
            </Box>)}
        </>
    );
});

AiChatFloating.displayName = 'AiChatFloating';

export { AiChatFloating };
